// src/Components/Modules/Inventory/DamageLossManagement.js
import React, { useState, useEffect } from 'react';
import { AxiosAuthInstance } from '../../../AxiosInterceptors';
import Breadcrumb from '../../Partials/Breadcrumb';
import {
    Box, Typography, Paper, Button, TextField, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
    DialogActions, Chip, FormControl, InputLabel, Select, MenuItem, Stack, IconButton
} from '@mui/material';
import { FaPlus, FaCheck, FaTimes, FaExclamationTriangle, FaTrash, FaEye } from 'react-icons/fa';
import Swal from 'sweetalert2';

export default function DamageLossManagement() {
    const [records, setRecords] = useState([]);
    const [inventory, setInventory] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    
    const [form, setForm] = useState({
        inventory_id: '',
        item_id: '',
        quantity: '',
        type: 'damaged',
        reason: ''
    });

    const fetchData = async () => {
        try {
            const [recRes, invRes] = await Promise.all([
                AxiosAuthInstance.get('damage-loss'),
                AxiosAuthInstance.get('inventories')
            ]);
            setRecords(recRes.data.records || []);
            setInventory(invRes.data.transactions || []);
        } catch (err) {
            console.error("Failed to fetch data:", err);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const handleSave = async () => {
        if (!form.inventory_id || !form.quantity || !form.reason) {
            Swal.fire({ icon: "error", title: "Error", text: "Please fill all required fields" });
            return;
        }

        try {
            await AxiosAuthInstance.post('damage-loss', form);
            Swal.fire({ 
                icon: 'success', 
                title: 'Recorded!', 
                text: 'Damage/Loss recorded and pending approval',
                timer: 2000, 
                showConfirmButton: false 
            });
            setDialogOpen(false);
            setForm({ inventory_id: '', item_id: '', quantity: '', type: 'damaged', reason: '' });
            fetchData();
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.error || 'Failed' });
        }
    };

    const handleApprove = async (id) => {
        try {
            await AxiosAuthInstance.post(`damage-loss/${id}/approve`);
            Swal.fire({ icon: 'success', title: 'Approved!', timer: 1500, showConfirmButton: false });
            fetchData();
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to approve' });
        }
    };

    const handleReject = async (id) => {
        try {
            await AxiosAuthInstance.post(`damage-loss/${id}/reject`);
            Swal.fire({ icon: 'success', title: 'Rejected!', timer: 1500, showConfirmButton: false });
            fetchData();
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to reject' });
        }
    };

    const getTypeColor = (type) => {
        const map = { damaged: 'error', lost: 'warning', expired: 'info' };
        return map[type] || 'default';
    };

    const getTypeLabel = (type) => {
        const map = { damaged: 'Damaged', lost: 'Lost', expired: 'Expired' };
        return map[type] || type;
    };

    const selectedItem = inventory.find(inv => inv.id === form.inventory_id);

    return (
        <Box sx={{ p: 3, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
            <Breadcrumb title="Damage & Loss Management" />
            
            <Paper sx={{ p: 3, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>Damage & Loss Records</Typography>
                    <Typography variant="body2" color="text.secondary">Track damaged, lost, or expired items</Typography>
                </Box>
                <Button variant="contained" startIcon={<FaPlus />} onClick={() => setDialogOpen(true)}>
                    Record Damage/Loss
                </Button>
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                            <TableCell>Date</TableCell>
                            <TableCell>Item</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Quantity</TableCell>
                            <TableCell>Total Loss</TableCell>
                            <TableCell>Reason</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {records.map((record) => (
                            <TableRow key={record.id} hover>
                                <TableCell>{new Date(record.created_at).toLocaleDateString()}</TableCell>
                                <TableCell>{record.item?.item_name || 'N/A'}</TableCell>
                                <TableCell>
                                    <Chip label={getTypeLabel(record.type)} size="small" color={getTypeColor(record.type)} />
                                </TableCell>
                                <TableCell>{record.quantity}</TableCell>
                                <TableCell>৳{Number(record.total_loss).toLocaleString()}</TableCell>
                                <TableCell>{record.reason}</TableCell>
                                <TableCell>
                                    <Chip 
                                        label={record.status} 
                                        size="small" 
                                        color={record.status === 'approved' ? 'success' : record.status === 'rejected' ? 'error' : 'warning'} 
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <Stack direction="row" spacing={1} justifyContent="center">
                                        <IconButton size="small" color="info" onClick={() => { setSelectedRecord(record); setViewDialogOpen(true); }}>
                                            <FaEye />
                                        </IconButton>
                                        {record.status === 'pending' && (
                                            <>
                                                <IconButton size="small" color="success" onClick={() => handleApprove(record.id)}>
                                                    <FaCheck />
                                                </IconButton>
                                                <IconButton size="small" color="error" onClick={() => handleReject(record.id)}>
                                                    <FaTimes />
                                                </IconButton>
                                            </>
                                        )}
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                        {records.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                    <FaExclamationTriangle size={48} style={{ color: '#bdbdbd', marginBottom: 12 }} />
                                    <Typography variant="body1" color="text.secondary">No damage/loss records found</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Record Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Record Damage/Loss</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <FormControl fullWidth size="small" required>
                            <InputLabel>Select Inventory Item</InputLabel>
                            <Select 
                                value={form.inventory_id} 
                                label="Select Inventory Item" 
                                onChange={(e) => {
                                    const inv = inventory.find(i => i.id === e.target.value);
                                    setForm({...form, inventory_id: e.target.value, item_id: inv?.item_id || ''});
                                }}
                            >
                                {inventory.map(inv => (
                                    <MenuItem key={inv.id} value={inv.id}>
                                        {inv.item?.item_name || 'Item'} - Stock: {inv.quantity}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth size="small" required>
                            <InputLabel>Type</InputLabel>
                            <Select value={form.type} label="Type" onChange={(e) => setForm({...form, type: e.target.value})}>
                                <MenuItem value="damaged">Damaged</MenuItem>
                                <MenuItem value="lost">Lost</MenuItem>
                                <MenuItem value="expired">Expired</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField 
                            label="Quantity *" 
                            type="number" 
                            size="small"
                            value={form.quantity} 
                            onChange={(e) => setForm({...form, quantity: e.target.value})} 
                            fullWidth 
                            required
                            inputProps={{ min: 0.01, step: 0.01 }}
                        />

                        <TextField 
                            label="Reason *" 
                            multiline 
                            rows={3}
                            value={form.reason} 
                            onChange={(e) => setForm({...form, reason: e.target.value})} 
                            fullWidth 
                            required
                            placeholder="Describe what happened..."
                        />

                        {selectedItem && (
                            <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                                <Typography variant="body2" fontWeight={600}>Current Stock: {selectedItem.quantity}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    After this: {selectedItem.quantity - parseFloat(form.quantity || 0)}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>Record</Button>
                </DialogActions>
            </Dialog>

            {/* View Dialog */}
            <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Damage/Loss Details</DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Type</Typography>
                                <Chip label={getTypeLabel(selectedRecord.type)} size="small" color={getTypeColor(selectedRecord.type)} />
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Item</Typography>
                                <Typography variant="body1">{selectedRecord.item?.item_name || 'N/A'}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Quantity</Typography>
                                <Typography variant="body1">{selectedRecord.quantity}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Unit Cost</Typography>
                                <Typography variant="body1">৳{Number(selectedRecord.unit_cost).toLocaleString()}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Total Loss</Typography>
                                <Typography variant="h6" color="error.main">৳{Number(selectedRecord.total_loss).toLocaleString()}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Reason</Typography>
                                <Typography variant="body1">{selectedRecord.reason}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Status</Typography>
                                <Chip label={selectedRecord.status} size="small" color={selectedRecord.status === 'approved' ? 'success' : 'warning'} />
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Date</Typography>
                                <Typography variant="body1">{new Date(selectedRecord.created_at).toLocaleString()}</Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}